import React from 'react';
import { DASH } from '@/utils/formatting';

/**
 * One status vocabulary for the whole app (UX Stage B, B3), and the badge that shows it: a dot and a label, coloured
 * by what the status MEANS, not by module:
 *   success  done, good (approved, posted, matched, kept)
 *   info     moving, or waiting on someone (in progress, submitted, suggested, posting)
 *   warning  needs a look but isn't wrong (partial, returned for changes, timing, in flux)
 *   danger   wrong, failed or refused (rejected, failed, exception, variance, broken)
 *   neutral  not begun, or finished without a verdict (not started, draft, unmatched, cancelled)
 *
 * Statuses come in UPPER_CASE (core reconciliations, periods), snake_case (bank rec, cash app, journals, SBL) and
 * with spaces (a few API labels); all are normalised to snake_case before lookup. The map was built from every
 * status the backend sends (models, CHECK constraints, API label maps). A status not in it shows its own words
 * ("with_owner" -> "With owner"), never "Unknown". Where one word means different things in two places, pass
 * `domain` (e.g. a LOCKED period is neutral, a locked bank rec is done).
 */
export type StatusTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';
export type StatusDomain = 'period' | 'close';

type Entry = readonly [StatusTone, string];

export const STATUS_VOCABULARY: Record<string, Entry> = {
  // Reconciliation sign-off (core), bank rec reconciliations, close runs
  not_started: ['neutral', 'Not started'],
  in_progress: ['info', 'In progress'],
  ready: ['info', 'Ready'],
  prepared: ['info', 'Prepared'],
  submitted: ['info', 'Submitted'],
  awaiting_approval: ['info', 'Awaiting approval'],
  approved: ['success', 'Approved'],
  certified: ['success', 'Certified'],
  locked: ['success', 'Locked'],
  returned: ['warning', 'Returned'],
  rejected: ['danger', 'Rejected'],
  proposed: ['neutral', 'Proposed'],
  ready_to_post: ['info', 'Ready to post'],
  recorded: ['success', 'Recorded'],
  superseded: ['neutral', 'Superseded'],
  variance: ['danger', 'Variance'],
  minor_variance: ['warning', 'Minor variance'],
  match: ['success', 'Matching'],
  in_flux: ['warning', 'In flux'],
  real: ['danger', 'Real difference'],
  ok: ['success', 'OK'],

  // Periods
  open: ['info', 'Open'],
  closed: ['neutral', 'Closed'],

  // Journals (core and bank rec)
  draft: ['neutral', 'Draft'],
  awaiting_netsuite: ['info', 'Awaiting NetSuite'],
  posting: ['info', 'Posting'],
  posted: ['success', 'Posted'],
  failed: ['danger', 'Failed'],
  cancelled: ['neutral', 'Cancelled'],
  found_by_amount: ['warning', 'Found by amount'],

  // Matching (bank rec lines and groups, cash app proposals, card settlements)
  unmatched: ['neutral', 'Unmatched'],
  suggested: ['info', 'Suggested'],
  matched: ['success', 'Matched'],
  confirmed: ['success', 'Confirmed'],
  auto: ['success', 'Auto-matched'],
  exception: ['danger', 'Exception'],
  timing: ['warning', 'Timing difference'],
  journalled: ['success', 'Journalled'],
  partial: ['warning', 'Partial'],
  query: ['danger', 'Query'],
  cleared: ['success', 'Cleared'],
  written_off: ['neutral', 'Written off'],
  statement_matched: ['info', 'Statement matched'],
  gl_matched: ['warning', 'GL matched'],
  fully_reconciled: ['success', 'Fully reconciled'],
  unreconciled: ['neutral', 'Unreconciled'],

  // Cash app: remittances, posting payments, disputes, promises, credit control
  received: ['neutral', 'Received'],
  linked: ['info', 'Linked'],
  applied: ['success', 'Applied'],
  withdrawn: ['neutral', 'Withdrawn'],
  pending: ['neutral', 'Pending'],
  with_owner: ['info', 'With owner'],
  resolved: ['success', 'Resolved'],
  credited: ['success', 'Credited'],
  kept: ['success', 'Kept'],
  part: ['warning', 'Part kept'],
  broken: ['danger', 'Broken'],
  on_hold: ['danger', 'On hold'],
  snoozed: ['neutral', 'Snoozed'],
  creditsafe_alert: ['warning', 'CreditSafe alert'],
  sla_missed: ['danger', 'Missed'],
  active: ['success', 'Active'],

  // SBL documents and checks, close rule versions
  complete: ['success', 'Complete'],
  error: ['danger', 'Error'],
  voided: ['danger', 'Voided'],
  pass: ['success', 'Passed'],
  fail: ['danger', 'Failed'],
  warn: ['warning', 'Check'],
  info: ['info', 'Information'],
  skip: ['neutral', 'Not run'],
  retired: ['neutral', 'Retired'],

  // Intercompany pairs (the matrix's states; IC_RECONCILIATION_PROPOSAL.md §4.5)
  agreed: ['success', 'Agreed'],
  fx_only: ['warning', 'FX only'],
  difference: ['danger', 'Difference'],
  one_sided: ['danger', 'One-sided'],
  no_balance: ['neutral', 'No balance'],
  unassigned: ['warning', 'Unassigned'],
  explained: ['success', 'Explained'],
  partly_explained: ['warning', 'Partly explained'],
  no_rate: ['danger', 'No rate'],
  disputed: ['danger', 'Disputed'],

  // Core and admin pages (help, import queue, notifications, users and keys, delegations)
  published: ['success', 'Published'],
  processing: ['info', 'Processing'],
  completed: ['success', 'Completed'],
  sent: ['info', 'Sent'],
  bounced: ['danger', 'Bounced'],
  opened: ['success', 'Opened'],
  clicked: ['success', 'Clicked'],
  inactive: ['neutral', 'Inactive'],
  expired: ['neutral', 'Expired'],
};

/** The same word meaning something else in one place. */
const DOMAIN: Record<StatusDomain, Record<string, Entry>> = {
  period: { open: ['success', 'Open'], locked: ['warning', 'Locked'], closed: ['neutral', 'Closed'] },
  close: { submitted: ['info', 'Awaiting approval'] },
};

export const normaliseStatus = (s: string) => s.trim().toLowerCase().replace(/[\s-]+/g, '_');

const humanise = (s: string) => {
  const t = s.trim().replace(/_/g, ' ').replace(/\s+/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
};

/** The tone and label for a status: for filters, selects and text that isn't a badge. */
export function statusInfo(status: string | null | undefined, domain?: StatusDomain): { tone: StatusTone; label: string; known: boolean } {
  if (status == null || !String(status).trim()) return { tone: 'neutral', label: DASH, known: false };
  const k = normaliseStatus(String(status));
  const hit = (domain && DOMAIN[domain][k]) || STATUS_VOCABULARY[k];
  return hit ? { tone: hit[0], label: hit[1], known: true } : { tone: 'neutral', label: humanise(String(status)), known: false };
}

/** A tone's fill alone (HeatMatrix's mirror cells pair it with muted text). TONE_CLASSES is built from it. */
export const TONE_FILL: Record<StatusTone, string> = {
  success: 'bg-mp-success-bg',
  info: 'bg-mp-info-bg',
  warning: 'bg-mp-warning-bg',
  danger: 'bg-mp-danger-bg',
  neutral: 'bg-mp-surface-2',
};
const TONE_TEXT: Record<StatusTone, string> = {
  success: 'text-mp-success', info: 'text-mp-info', warning: 'text-mp-warning', danger: 'text-mp-danger',
  neutral: 'text-mp-text-muted',
};

export const TONE_CLASSES: Record<StatusTone, string> = Object.fromEntries(
  (Object.keys(TONE_FILL) as StatusTone[]).map(t => [t, `${TONE_FILL[t]} ${TONE_TEXT[t]}`]),
) as Record<StatusTone, string>;

export const StatusBadge: React.FC<{ status: string | null | undefined; domain?: StatusDomain; label?: string; tone?: StatusTone;
                                     title?: string; className?: string }> = (
  { status, domain, label, tone, title, className = '' },
) => {
  const info = statusInfo(status, domain);
  return (
    <span title={title} data-status={status ? normaliseStatus(String(status)) : undefined} data-tone={tone ?? info.tone}
          className={`inline-flex shrink-0 items-center gap-mp-1 whitespace-nowrap rounded-mp-sm px-mp-2 py-px text-mp-sm font-medium ${TONE_CLASSES[tone ?? info.tone]} ${className}`.trim()}>
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
      {label ?? info.label}
    </span>
  );
};
